import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../Pagination';
import { fmtDateTime } from '../../utils/region';
import { coaLogApi, ChangeLogPage } from '../../utils/api/coalog';
import { ChangeLine } from '../Bookkeeping/AccountHistory';

// ── Admin → Chart template → Change history and integrity (COA-10) ──────────────────────────────
// The chart of accounts' change log across every client and the house template, newest first, with
// the two checks server/src/db/coaLog.ts makes against the charts themselves:
//   · RECORDED LATE — accounts a start-up found with no history at all (written outside the recorder);
//   · DO NOT MATCH  — accounts whose log, replayed, does not give the row in the table (changed
//     outside the recorder). Counted by the server on this request, not remembered from a boot.
// ⛔ READ-ONLY, like the rest of this tab: GET /api/admin/coa-master/changes, behind requireAdmin.
// ⛔ Every figure and every row is the server's; names, codes, values, people and sources are shown
// as recorded. Only the labels are in src/i18n/mt/coalog.ts.

const SCOPES = ['client_chart', 'house_template', 'master_firm_layer', 'asset_category'];
const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: 16, marginBottom: 14 };
const muted: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 };
const field: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '9px 10px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

/** `refreshKey`: the tab bumps it after it records a firm judgement, so the log shows the new line. */
export default function CoaChangeLog({ refreshKey = 0 }: { refreshKey?: number } = {}) {
  const { t } = useTranslation();
  const [data, setData] = useState<ChangeLogPage | null>(null);
  const [clientId, setClientId] = useState('');
  const [scope, setScope] = useState('');
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setErr('');
    coaLogApi.changes({ clientId, scope, page })
      .then(d => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, scope, page, refreshKey]);

  const scopeLabel = (s: string) => (SCOPES.includes(s) ? t(`coal_scope_${s}`) : s);
  const integ = data?.integrity;
  const tiles: [string, string, number, string?][] = data && integ ? [
    ['rows', t('coal_tile_rows'), data.total],
    ['baselines', t('coal_tile_baselines'), integ.baselines],
    ['late', t('coal_tile_late'), integ.baselineLate, t('coal_tile_late_hint')],
    ['replay', t('coal_tile_replay'), integ.replay.mismatched, t('coal_tile_replay_hint', { n: integ.replay.accounts })],
  ] : [];

  return (
    <section data-section="coa-changes" style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <i className="ti ti-history" style={{ fontSize: 18, color: 'var(--text-secondary)' }} aria-hidden />
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{t('coal_admin_title')}</div>
      </div>
      <div style={{ ...muted, marginBottom: 10 }}>{t('coal_admin_lead')}</div>
      {err && <div role="alert" style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 10 }}>{t('coal_load_failed', { error: err })}</div>}

      {data && integ && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
            {tiles.map(([key, label, n, hint]) => {
              const alarm = (key === 'late' || key === 'replay') && n > 0;
              return (
                <div key={key} data-coal-tile={key} title={hint}
                  style={{ minWidth: 130, padding: '8px 12px', borderRadius: 10, border: `1px solid ${alarm ? '#F5D9A8' : 'var(--border)'}`, background: alarm ? '#FEF6E7' : 'var(--bg-app)' }}>
                  <div data-coal-value style={{ fontSize: 20, fontWeight: 800, color: alarm ? '#92400E' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{n}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{label}</div>
                </div>
              );
            })}
          </div>
          <div style={{ ...muted, fontSize: 11.5 }}>{t('coal_tile_late_hint')} {t('coal_tile_replay_hint', { n: integ.replay.accounts })}</div>
          {integ.columns.untracked.length > 0 && (
            <div data-coal-untracked style={{ fontSize: 12.5, color: '#92400E', marginTop: 6 }}>{t('coal_untracked', { cols: integ.columns.untracked.join(', ') })}</div>
          )}
          {integ.replay.sample.length > 0 && (
            <div data-coal-mismatches style={{ ...muted, marginTop: 6 }}>
              {t('coal_mismatch_lead')}{' '}
              {integ.replay.sample.map((m, i) => (
                <span key={m.id} data-mismatch={m.id}>
                  {i ? ' · ' : ''}<b>{m.code}</b> <code style={{ fontSize: 11 }}>{m.id}</code>{' '}
                  ({m.why === 'differs' ? t('coal_why_differs', { cols: m.columns.join(', ') })
                    : m.why === 'gap' ? t('coal_why_gap', { cols: m.columns.join(', ') }) : t(`coal_why_${m.why}`)})
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '12px 0 8px' }}>
            <select aria-label={t('coal_filter_client')} value={clientId} onChange={e => { setPage(1); setClientId(e.target.value); }} style={field}>
              <option value="">{t('coal_all_clients')}</option>
              {data.clients.map(c => <option key={c.id} value={c.id}>{c.name || c.id} ({c.n})</option>)}
            </select>
            <select aria-label={t('coal_filter_scope')} value={scope} onChange={e => { setPage(1); setScope(e.target.value); }} style={field}>
              <option value="">{t('coal_all_scopes')}</option>
              {data.scopes.map(s => <option key={s.scope} value={s.scope}>{scopeLabel(s.scope)} ({s.n})</option>)}
            </select>
            <span data-coal-count style={{ ...muted, fontSize: 12 }}>{t('coal_rows', { count: data.total })}</span>
          </div>

          {data.rows.length === 0 ? (
            <div style={{ ...muted, color: 'var(--text-tertiary)' }}>{t('coal_none')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table aria-label={t('coal_admin_title')} style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['coal_col_when', 'coal_col_client', 'coal_col_account', 'coal_col_change', 'coal_col_who', 'coal_col_source'].map(k => <th key={k} style={th}>{t(k)}</th>)}
                </tr></thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.id} data-log-id={r.id}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.at)}</td>
                      <td style={td}>{r.clientId ? (r.clientName || r.clientId) : r.scope === 'master_firm_layer' ? t('coal_scope_master_firm_layer') : t('coal_house')}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.code}</td>
                      <td style={td}><ChangeLine row={r} refs={data.refs?.[r.clientId]} />{r.reason && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.reason}</div>}</td>
                      <td style={td}>{r.actorName}</td>
                      <td style={{ ...td, fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={data.page} total={data.total} limit={data.limit} onPage={setPage} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
